import * as React from 'react';
import { connect } from 'react-redux'; // Connecting to Redux
import { Form, Select, Col, Row, Tabs, Button, Icon, Popover, message, Checkbox } from 'antd';
import moment from 'moment';
import { Moment } from 'moment';
import { FormComponentProps } from 'antd/lib/form/Form';
import { withLocalize, LocalizeContextProps, Translate } from 'react-localize-redux';
import bindAllOfThis from 'src/utils/BindThisHelper'; // 'this' binder
import FieldDecorator from './form/FieldDecorator';
import { NameOf } from 'src/utils/NameOf';
import { Grades } from 'src/constants/Grades';
import LetterRangeSelector from './form/LetterRangeSelector';
import ReportHeaderDTO from 'src/models/report/ReportHeaderDTO';
import { ActivityTypes } from 'src/constants/ActivityType';
import ObjectUtil from 'src/utils/ObjectUtil';
import { ReduxStoreModel } from '../../redux/ReduxModel';
import { UserState } from 'src/redux/UserReducer';
import SchoolApiController from 'src/api/SchoolApiController';
import KeyValueDTO from 'src/models/generic/KeyValueDTO';
import "./ReportHeader.less";

interface ReportHeaderProps extends FormComponentProps, LocalizeContextProps {
    isLoading: boolean;
    reportingEvent?: "Activity" | "I Statement" | "Planet Completion" | "Game Completion";

    /** Indicates how many school years are in the reporting period dropdown. Always includes the current school year */
    defaultSchoolYears?: "Current" | "Previous 1" | "Previous 2" | "Previous 3"; // 'Default' because school year calculations are hard and done in constructor
    defaultShowSchoolSelection?: boolean;

    onSubmit: (values: Partial<ReportHeaderDTO>) => void;
    onReset: () => void;

    // From Redux
    User: UserState;
}

interface SchoolYear {
    id: number;
    name: "Current" | "Previous 1" | "Previous 2" | "Previous 3";
    lower: Moment;
    upper: Moment;
    label?: string;
}

interface ColBreakpoints {
     xs?: number;
     sm?: number;
     md?: number;
     lg?: number;
     xl?: number;
     xxl?: number;
}

interface ReportHeaderState {
    isResetting: boolean;
    isSchoolLoading: boolean;
    showReportingPeriodPopup: boolean;
    showCurrentGradePopup: boolean;
    formValues: Partial<ReportHeaderDTO>;

    schoolYears: SchoolYear[];
    schools: KeyValueDTO<number, string>[];
}

class ReportHeader extends React.Component<ReportHeaderProps, ReportHeaderState> {
    private defaults: Partial<ReportHeaderProps> = {
        defaultSchoolYears: "Previous 2",
        defaultShowSchoolSelection: false
    };

    constructor(props: ReportHeaderProps) {
        super(props);
        bindAllOfThis(this, ReportHeader.prototype);

        let today = moment(); // Leaving this here for easier testing
        let isStartOfSchoolYear = moment(today).month(6).endOf('month').isBefore(today);
        let startOfCurrent = isStartOfSchoolYear ? moment(today).month(7).startOf('month') : moment(today).month(7).startOf('month').subtract(1, 'year');
        let current: SchoolYear = { id: 1, lower: startOfCurrent, upper: moment(startOfCurrent).add(1, 'year'), name: "Current" };

        let schoolYears: SchoolYear[] = [
            current,
            { id: current.id + 1, lower: moment(current.lower).subtract(1, 'year'), upper: moment(current.upper).subtract(1, 'year'), name: "Previous 1" },
            { id: current.id + 2, lower: moment(current.lower).subtract(2, 'year'), upper: moment(current.upper).subtract(2, 'year'), name: "Previous 2" },
            { id: current.id + 3, lower: moment(current.lower).subtract(3, 'year'), upper: moment(current.upper).subtract(3, 'year'), name: "Previous 3" }
        ];
        // Generate the labels
        schoolYears.forEach(x => (x.label = `${x.lower.year()} - ${x.upper.year()} School Year`));

        // Reduce school years to match what is selected
        switch (props.defaultSchoolYears) {
            case "Current":
                schoolYears = schoolYears.slice(0, 1);
                break;
            case "Previous 1":
                schoolYears = schoolYears.slice(0, 2);
                break;
            case "Previous 2":
                schoolYears = schoolYears.slice(0, 3);
                break;
            case "Previous 3":
                schoolYears = schoolYears.slice(0, 4);
                break;
            default:
                break;
        }

        this.state = {
            isResetting: false,
            isSchoolLoading: true,
            showReportingPeriodPopup: false,
            showCurrentGradePopup: false,
            formValues: {
                eventTypeId: null,
                gradeIds: [], //Grades.All.map(x => x.id),
                reportPeriod: null,
                reportPeriodType: null,
                firstName: { lower: "A", upper: "Z" },
                lastName: { lower: "A", upper: "Z" }
            },
            schoolYears,
            schools: []
        };
    }

    componentDidMount() {
        this.getSchools();
    }

    private getProps(props: ReportHeaderProps = this.props) {
        return ObjectUtil.DeepMergeObjects(this.defaults, props);
    }

    private getSchools() {
        let props = this.getProps();
        // Load schools for current user account
        if (!props.defaultShowSchoolSelection) {
            this.setState({ isSchoolLoading: false });
            return;
        }

        return SchoolApiController.getSchoolsForCurrentUser().then((result) => {
            let schools = result.data.sort((a, b) => a.value < b.value ? -1 : a.value > b.value ? 1 : 0 );
            this.setState({ schools, isSchoolLoading: false });
        }).catch(e => {
            message.error(this.props.translate('admin.addEditSchool.messageRetrieveError'));
        });
    }

    private handleReset() {
        this.setState({
            isResetting: true
        }, () => this.props.form.resetFields());
    }

    private handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();

        // Don't validate on reset
        if (this.state.isResetting) {
            this.setState({ isResetting: false });
            if (this.props.onReset != null) {
                this.props.onReset();
            }
            return;
        }

        // Make sure all fields are valid
        this.props.form.validateFields((errors, values: Partial<ReportHeaderDTO>) => {
            if (!!errors) {
                return;
            }

            // Reduce 'values' to just the values that have values
            var reducedValues: Partial<ReportHeaderDTO> = {};
            Object.keys(values).forEach(x => {
                let newVal = values[x];
                if (newVal?.toString().length > 0) {
                    reducedValues[x] = newVal;
                }
            });

            // Custom replacer since report period is now a select not another component
            if (reducedValues.reportPeriod != null) {
                // Antd puts the value, a number, into the reportPeriod slot so we will replace it with the proper value from state
                let schoolYear = this.state.schoolYears.find(x => x.id === (reducedValues as any).reportPeriod);
                reducedValues.reportPeriod = schoolYear;
                reducedValues.reportPeriodType = schoolYear.name;
            }

            if (this.props.onSubmit != null) {
                this.props.onSubmit(reducedValues);
            }
        });
    }

    private renderSchoolSelection(breakpoints: ColBreakpoints) {
        let props = this.getProps();
        let child: JSX.Element = null;

        // Show schools for current role if we are allowed
        if (!!props.defaultShowSchoolSelection) {
            child = (
                <FieldDecorator
                    form={this.props.form}
                    label={"School"}
                    name={NameOf<ReportHeaderDTO>("schoolId")}
                    initialValue={this.props.User.schoolId}
                    rules={[
                        { required: true, message: <Translate id="admin.common.requiredRule" /> },
                    ]}
                >
                    <Select disabled={this.state.isSchoolLoading || this.state.schools.length === 1}>
                        {
                            this.state.schools?.map(x => <Select.Option key={x.key} value={x.key} title={x.value}>{x.value}</Select.Option>)
                        }
                    </Select>
                </FieldDecorator>
            );
        }

        return child != null ? <Col {...breakpoints}>{child}</Col> : null;
    }

    private renderReportingEvent(breakpoints: ColBreakpoints) {
        let props = this.getProps();
        let data = this.state.formValues;
        let child: JSX.Element = null;

        if (props.reportingEvent === "Activity") {
            child = (
                <FieldDecorator
                    form={this.props.form}
                    label={"Activity Type"}
                    name={NameOf<ReportHeaderDTO>("eventTypeId")}
                    initialValue={data.eventTypeId}
                    rules={[
                        { required: true, message: <Translate id="admin.common.requiredRule" /> },
                    ]}
                >
                    <Select
                        placeholder={"Select Activity type - Surveys or Quiz"}
                        allowClear
                        filterOption={(input, option) => option.props.title.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                        {...{ showAction: ["focus", "click"] }}
                    >
                        {
                            ActivityTypes.All.map(x => <Select.Option key={x.id} value={x.id} title={x.name}>{x.name}</Select.Option>)
                        }
                    </Select>
                </FieldDecorator>
            );
        }

        return child != null ? <Col {...breakpoints}>{child}</Col> : null;
    }

    private renderReportingPeriod(breakpoints: ColBreakpoints) {
        let props = this.getProps();
        let data = this.state.formValues;
        let child: JSX.Element = null;

        if (["I Statement", "Planet Completion"].some(x => x === props.reportingEvent)) {
            let popoverContent: React.ReactNode = <div>
                <p>Report period is setup to use school years. School years start on August 1st and end on July 31st.</p>
                <p>Available in a future release!</p>
            </div>;
            child = (
                <FieldDecorator
                    form={this.props.form}
                    label={<React.Fragment>
                        Report Period
                        <Popover trigger="click" title="School Years" content={popoverContent}>
                            <Icon className="info-icon" type="info-circle" onClick={() => this.setState({ showReportingPeriodPopup: true })} />
                        </Popover>
                    </React.Fragment>}
                    name={NameOf<ReportHeaderDTO>("reportPeriod")}
                    initialValue={data.reportPeriod}
                >
                    <Select disabled>
                        <Select.Option value={1} title="N/A">N/A</Select.Option>
                    </Select>
                </FieldDecorator>
            );
        } else {
            let popoverContent: React.ReactNode = "Report period is setup to use school years. School years start on August 1st and end on July 31st.";
            child = (
                <FieldDecorator
                    form={this.props.form}
                    label={<React.Fragment>
                        Report Period
                        <Popover trigger="click" title="School Years" content={popoverContent}>
                            <Icon className="info-icon" type="info-circle" onClick={() => this.setState({ showReportingPeriodPopup: true })} />
                        </Popover>
                    </React.Fragment>}
                    name={NameOf<ReportHeaderDTO>("reportPeriod")}
                    initialValue={data.reportPeriod}
                    rules={[
                        { required: true, message: <Translate id="admin.common.requiredRule" /> },
                    ]}
                >
                    <Select
                        allowClear
                        {...{ showAction: ["focus", "click"] }}
                    >
                        {
                            this.state.schoolYears.map(data => {
                                return <Select.Option key={data.id} value={data.id} title={data.label}>{data.label}</Select.Option>;
                            })
                        }
                    </Select>
                </FieldDecorator>
            );
        }

        return child != null ? <Col {...breakpoints}>{child}</Col> : null;
    }

    render() {
        let data = this.state.formValues;

        let breakpoints: ColBreakpoints = {
            xs: 12,
            lg: 8
        };

        return (
            <div className="report-header">
                <Form layout="vertical" onSubmit={this.handleSubmit}>
                    <Row type="flex" justify="start">
                        <Col xs={24}>
                            <Row gutter={16}>
                                {this.renderSchoolSelection(breakpoints)}
                                {this.renderReportingEvent(breakpoints)}
                                {this.renderReportingPeriod(breakpoints)}

                                <Col {...breakpoints}>
                                    <FieldDecorator
                                        form={this.props.form}
                                        label={<React.Fragment>
                                            Current Grade
                                            <Popover trigger="click" title="School Years" content={"Filter for the Current Grade Level of the students."}>
                                                <Icon className="info-icon" type="info-circle" onClick={() => this.setState({ showReportingPeriodPopup: true })} />
                                            </Popover>
                                        </React.Fragment>}
                                        name={NameOf<ReportHeaderDTO>("gradeIds")}
                                        initialValue={data.gradeIds}
                                        getValueFromEvent={FieldDecorator.StandardSelectAllEvent("all", Grades.All.map(x => x.id))}
                                        rules={[
                                            { required: true, message: <Translate id="admin.common.requiredRule" /> },
                                        ]}
                                    >
                                        <Select
                                            mode="multiple"
                                            placeholder={"Select grade between 'PK to Grade 5'"}
                                            allowClear
                                            filterOption={(input, option) => option.props.title.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                                            {...{ showAction: ["focus", "click"] }}
                                        >
                                            <Select.Option key={"all"} value={"all"}>
                                                {this.props.form.getFieldValue(NameOf<ReportHeaderDTO>("gradeIds"))?.length < Grades.All.length ? "All" : "None"}
                                            </Select.Option>
                                            {
                                                Grades.All.map(x => <Select.Option key={x.id} value={x.id} title={x.shortName}>{x.shortName}</Select.Option>)
                                            }
                                        </Select>
                                    </FieldDecorator>
                                </Col>
                            </Row>
                            <Row gutter={16} type="flex" align="bottom">
                                <Col {...breakpoints}>
                                    <FieldDecorator
                                        form={this.props.form}
                                        label={"First Name Starts With"}
                                        name={NameOf<ReportHeaderDTO>("firstName")}
                                        initialValue={data.firstName}
                                    >
                                        <LetterRangeSelector renderMode="Hidden" />
                                    </FieldDecorator>
                                </Col>
                                <Col {...breakpoints}>
                                    <FieldDecorator
                                        form={this.props.form}
                                        label={"Last Name Starts With"}
                                        name={NameOf<ReportHeaderDTO>("lastName")}
                                        initialValue={data.lastName}
                                    >
                                        <LetterRangeSelector renderMode="Hidden" />
                                    </FieldDecorator>
                                </Col>
                                <Col xs={24} lg={8}>
                                    <Row className="form-buttons" type="flex" justify="end">
                                        <Button type="primary" icon="download" loading={this.props.isLoading} htmlType="submit">Generate</Button>
                                        <Button type="default" onClick={this.handleReset} htmlType="submit">Clear</Button>
                                    </Row>
                                </Col>
                                {/* <Col xs={0} lg={8}>
                                    <Row className="form-buttons" type="flex" justify="end">
                                        <Button type="primary" icon="download" loading={this.props.isLoading} htmlType="submit">Generate</Button>
                                        <Button type="default" onClick={this.handleReset} htmlType="submit">Clear</Button>
                                    </Row>
                                </Col> */}
                            </Row>
                        </Col>
                    </Row>
                </Form>
            </div>);
    }
}

function mapStateToProps(state: ReduxStoreModel) {
    return {
        User: state.User
    };
}

export default connect(mapStateToProps)(withLocalize(Form.create()(ReportHeader)));



// WEBPACK FOOTER //
// ./src/components/common/ReportHeader.tsx